import { useMsal, useAccount } from '@azure/msal-react';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, silentRequest } from './authConfig';
import { useCallback } from 'react';

interface UseAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export const useAuth = (): UseAuthResult => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);

  const isAuthenticated = accounts.length > 0;
  const isLoading = inProgress !== InteractionStatus.None;

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...silentRequest,
        account: account,
      });
      // Return ID token (contains roles) instead of access token
      return response.idToken;
    } catch (error) {
      console.error('Failed to acquire token silently:', error);
      // If silent acquisition fails, try interactive
      try {
        await instance.acquireTokenRedirect(loginRequest);
        return null; // Will redirect, so return null
      } catch (interactiveError) {
        console.error('Interactive token acquisition failed:', interactiveError);
        return null;
      }
    }
  }, [instance, account]);

  return {
    isAuthenticated,
    isLoading,
    user: account,
    login,
    logout,
    getAccessToken,
  };
};
